import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@services/apiService';

export type TeamFeature = {
  enabled: boolean;
  feature: {
    id: string;
    name: string;
    description: string;
  };
  config?: Record<string, any>;
};

function teamFeatureQuery(team: string) {
  return {
    suspense: true,
    queryKey: ['admin', 'teams', team, 'features'],
    queryFn: async () => {
      return api.get(`admin/teams/${team}/features`).json<TeamFeature[]>();
    },
  };
}

export function useTeamFeatures(team: string) {
  return useQuery(teamFeatureQuery(team));
}

export interface ToggleFeatureWithConfigOptions {
  enabled: boolean;
  config?: Record<string, any>;
}
export function useToggleFeatureWithConfig(team: string, feature: string) {
  const client = useQueryClient();
  const queryKey = teamFeatureQuery(team).queryKey;

  return useMutation({
    mutationFn: async (options: ToggleFeatureWithConfigOptions) => {
      return api.patch<TeamFeature>(`admin/teams/${team}/features/${feature}`, {
        json: options,
      });
    },
    onMutate: async (options: ToggleFeatureWithConfigOptions) => {
      await client.cancelQueries(queryKey);
      const previousValue = client.getQueryData(queryKey);
      client.setQueryData<TeamFeature[]>(queryKey, (old) => {
        return (old || []).map((teamFeature) => {
          if (teamFeature.feature.id === feature) {
            return { ...teamFeature, ...options };
          }
          return teamFeature;
        });
      });

      return { previousValue };
    },
    onError: (_, __, context) => {
      client.setQueryData(queryKey, context?.previousValue);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey });
    },
  });
}
